import $ from 'jquery';
import _ from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { AlertNotificationConstant, StorageConstant } from '../constants';
import { storageService } from '@life-time-inc/enterprise-framework-core';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

export default class AlertNotificationService implements ltFramework.IAlertNotificationService {
	public openAlertNotification(
		alertNotification: ltFramework.IAlertNotification,
		currentAlertNotifications: ltFramework.IAlertNotification[]
	): ltFramework.IAlertNotification {
		let message = '';
		let dismissibleClass = '';
		let dismissibleButton = '';
		let iconClass = '';
		const element: JQuery<HTMLElement> = $(AlertNotificationConstant.alertNotificationElementId);
		let storageAlertNotifications: ltFramework.IAlertNotification[] = storageService.sessionGet<
			ltFramework.IAlertNotification[]
		>(StorageConstant.alertNotifications) as ltFramework.IAlertNotification[];
		const dismissedNotifications: ltFramework.IAlertNotification[] = storageService.localGet<
			ltFramework.IAlertNotification[]
		>(StorageConstant.dismissedAlertNotifications) as ltFramework.IAlertNotification[];
		if (!storageAlertNotifications) {
			storageAlertNotifications = [];
		}

		if (!alertNotification.id) {
			alertNotification.id = uuidv4();
		}

		const existsInStore: boolean = _.some(
			currentAlertNotifications,
			(temp: ltFramework.IAlertNotification) => {
				return temp.id === alertNotification.id;
			}
		);

		const existsInStorage: boolean = _.some(
			storageAlertNotifications,
			(temp: ltFramework.IAlertNotification) => {
				return temp.id === alertNotification.id;
			}
		);

		if (existsInStore || existsInStorage) {
			throw new Error(
				"The alert notification id '" + alertNotification.id + "' is already defined."
			);
		}

		this.validateAlertNotification(alertNotification);

		if (alertNotification.dismissible) {
			dismissibleClass = 'alert-dismissible';
			dismissibleButton = `<button type="button" class="close" data-guid="${alertNotification.id}" data-end-date="${alertNotification.endDate}" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">×</span></button>`;
		}

		// add markup to message
		// checking length to see if we need truncation classes
		const truncateLength = alertNotification.truncateLength
			? alertNotification.truncateLength
			: 150;
		if (alertNotification.message.length > truncateLength) {
			message = `
				<div id="${alertNotification.id}" class="truncate m-b-sm">
					<div class="truncate-brief">
						<p>${alertNotification.message.substring(0, truncateLength - 1)}... <a href="#${alertNotification.id}" data-toggle="truncate">View More</a></p>
					</div>
					<div class="truncate-full">
						<p>${alertNotification.message} <a href="#${alertNotification.id}" data-toggle="truncate">View Less</a></p>
					</div>
				</div>
			`;
		} else {
			message = `<p>${alertNotification.message}</p>`;
		}

		if (alertNotification.title) {
			message = '<p class="m-b-0"><strong>' + alertNotification.title + '</strong></p>' + message;
		}

		if (alertNotification.startDate && alertNotification.club) {
			const clubName = window.ef.clubStore.getInitialData().club.name;
			message += `<p class="micro">${clubName} &ndash; ${moment(alertNotification.startDate, moment.ISO_8601).fromNow()}</p>`;
		}

		if (alertNotification.icon) {
			iconClass = 'alert-icon';
		}

		const template = `
			<div id="${AlertNotificationConstant.alertNotificationPrefix}${alertNotification.id}" class="alert ${alertNotification.type} ${iconClass} alert-notification ${dismissibleClass} hidden-print m-b-0 fade in">
				<div class="container">
					${dismissibleButton}
					<div role="alert">${message}</div>
				</div>
			</div>
		`;

		if (alertNotification.defer) {
			storageAlertNotifications.push(alertNotification);

			storageService.sessionSet(
				StorageConstant.deferredAlertNotifications,
				storageAlertNotifications
			);
		}

		const isExpired = this.isAlertNotificationExpired(
			alertNotification.startDate as moment.Moment,
			alertNotification.endDate as moment.Moment
		);
		const isDismissed = this.isAlertNotificationDismissed(
			alertNotification.id,
			dismissedNotifications
		);
		if (!isExpired && !isDismissed && !alertNotification.defer) {
			const alertSelector =
				'#' + AlertNotificationConstant.alertNotificationPrefix + alertNotification.id;

			if ($(alertSelector).length) {
				$(alertSelector).replaceWith(template);
			} else {
				element.append(template);
			}
			$(alertSelector).on('closed.bs.alert', () => {
				this.closeAlertNotification(alertNotification);
			});
			if (!alertNotification.disableScroll) {
				$('html, body').animate({ scrollTop: 0 }, 'fast');
			}
		}

		return alertNotification;
	}

	public closeAlertNotification(alertNotification: ltFramework.IAlertNotification): void {
		$('#' + AlertNotificationConstant.alertNotificationPrefix + alertNotification.id).alert(
			'close'
		);
		// add to storage
		const dismissedNotifications: ltFramework.IDismissedAlertNotification[] =
			storageService.localGet(
				StorageConstant.dismissedAlertNotifications
			) as ltFramework.IDismissedAlertNotification[];
		dismissedNotifications.push({ id: alertNotification.id, endDate: alertNotification.endDate });
		// if the dismissed notification is for onhold or student reactivations, only set for the session
		if (alertNotification.id === AlertNotificationConstant.alertNotificationStudentId) {
			storageService.sessionSet(AlertNotificationConstant.alertNotificationStudentDismissed, true);
		} else if (alertNotification.id === AlertNotificationConstant.alertNotificationOnHoldId) {
			storageService.sessionSet(AlertNotificationConstant.alertNotificationOnHoldDismissed, true);
		} else {
			storageService.localSet(StorageConstant.dismissedAlertNotifications, dismissedNotifications);
		}
	}

	public validateAlertNotification(alertNotification: ltFramework.IAlertNotification): void {
		if (!alertNotification.id) {
			throw new Error('The alert notification id is not defined.');
		}

		if (!alertNotification.type) {
			throw new Error('The alert notification type is not defined.');
		}

		if (!alertNotification.message) {
			throw new Error('The alert notification message is not defined.');
		}

		if (alertNotification.startDate && !alertNotification.endDate) {
			throw new Error('The alert notification end date is not defined.');
		}

		if (!alertNotification.startDate && alertNotification.endDate) {
			throw new Error('The alert notification start date is not defined.');
		}

		alertNotification.message = alertNotification.message.replace(
			/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
			''
		);
	}

	public isAlertNotificationExpired(startDate: moment.Moment, endDate: moment.Moment): boolean {
		const currentDate: moment.Moment = moment();

		let isStartDateValid = true;
		if (startDate) {
			isStartDateValid = currentDate.isSameOrAfter(startDate);
		}

		let isEndDateValid = true;
		if (endDate) {
			isEndDateValid = currentDate.isSameOrBefore(endDate);
		}

		return !(isStartDateValid && isEndDateValid);
	}

	public isAlertNotificationDismissed(
		id: string,
		dismissedNotifications: ltFramework.IDismissedAlertNotification[]
	): boolean {
		let dismissed = false;
		_.forEach(dismissedNotifications, (dn: ltFramework.IDismissedAlertNotification) => {
			if (dn.id === id) {
				dismissed = true;
			}
		});

		return dismissed;
	}

	public clearExpiredDismissedNotifications(
		dismissedNotifications: ltFramework.IDismissedAlertNotification[]
	) {
		let cleanData = [];
		cleanData = _.remove(dismissedNotifications, (dn: ltFramework.IDismissedAlertNotification) => {
			return dn.endDate && moment(dn.endDate, moment.ISO_8601).isAfter(moment());
		});
		storageService.localSet(StorageConstant.dismissedAlertNotifications, cleanData);
	}

	public displayAuthoredNotifications(sdids: string[]): void {
		const notificationContainers = $('.alert-notification-authored');
		notificationContainers.each((index, notificationContainer) => {
			if (!_.includes(sdids, $(notificationContainer).attr('id'))) {
				$(notificationContainer).removeClass('hide');
			} else {
				$(notificationContainer).addClass('hide');
			}
		});
	}
}

export const alertNotificationService = new AlertNotificationService();
